import React, {useMemo} from "react";
import {rootStore, StoreContext} from "stores/rootStore";
import {HelmetProvider} from 'react-helmet-async';
import {isWidgetRoute} from "utils";
import {AuthProvider} from "@it-s-ai/auth";
import {ADMIN_SECRET, BACKEND_URL, GOOGLE_CLIENT_ID} from "config";
import {getClientIdFromCookie} from "utils";
import {getUniqueToken} from "persistence";
import {getPendingGuestSession} from "guestCheckout";
import {DevAccessGuard} from "components/DevAccessGuard";
import {getLanguage} from "i18n/LanguageProvider";
import {logEvent} from "analytics";
import {gtmName} from 'utils/gtm';
import type {Locale} from "@it-s-ai/auth";
import {toAuthLocale} from "i18n/authLocale";
import {signupArms} from "utils/abTest";

// Runs outside LanguageProvider, so the language comes off the URL prefix.
function getAuthLocale(): Locale {
    return toAuthLocale(window.location.pathname.match(/^\/([a-z]{2})\//)?.[1]);
}

export const ContextComponent = (props: { children: React.ReactNode }) => {

    const inner = (
        <StoreContext.Provider value={rootStore}>
            {props.children}
        </StoreContext.Provider>
    );

    // Widget routes use external token auth — skip AuthProvider
    // to avoid stale sessions conflicting with the passed token
    if (isWidgetRoute()) {
        console.log("Skip auth provider")
        return <HelmetProvider>{inner}</HelmetProvider>;
    }

    return (
        <DevAccessGuard>
            <HelmetProvider>
                <AuthProvider config={{
                    googleClientId: GOOGLE_CLIENT_ID || '',
                    backendUrl: BACKEND_URL || '',
                    callbackPath: '/auth/callback',
                    onboardingEnabled: true,
                    getLocale: getAuthLocale,
                    // Local dev only: REACT_APP_ADMIN_SECRET is set just in the
                    // local env, so email login skips OTP and logs in instantly.
                    // Undefined on dev/prod builds → normal OTP flow.
                    devOtpBypassCode: ADMIN_SECRET,
                    getAuthParams: () => {
                        // Pay-first guest checkout: when present, the backend moves the
                        // anon's Stripe purchase onto this account during signup/login.
                        const guestSession = getPendingGuestSession();
                        return {
                            service: "app",
                            platform: "web",
                            client_id: getClientIdFromCookie(),
                            anon_username: getUniqueToken(),
                            // Sent with every auth call, and stored on the account:
                            // it is the only language hint the backend has when it
                            // mails the login code, and later — from a Stripe
                            // webhook, with no request around — the subscription and
                            // refund emails.
                            locale: getAuthLocale(),
                            ...(guestSession ? {session_id: guestSession.sessionId} : {}),
                            // Kept by the backend on a new account only (bitapi.users.experiments).
                            // Only an anon signs up, so an account's own calls (the survey) roll
                            // no arm; `is_anon` rather than isAnonymous, which a report view forces.
                            ab_arms: (rootStore.user.user?.is_anon ?? true) ? signupArms() : {},
                        };
                    },
                    logEvent: (event, params) => {
                        const common = {
                            page_path: window.location.pathname,
                            page_language: getLanguage(),
                            page_type: 'app_page',
                            user_state: rootStore.user.isAnonymous ? 'anonymous' : 'authenticated',
                            entrypoint: 'auth_modal',
                        };
                        return logEvent({event: gtmName(event), params: {...common, ...params}});
                    },
                }}>
                    {inner}
                </AuthProvider>
            </HelmetProvider>
        </DevAccessGuard>
    )
}
